<template>
  <div class="rp">
    <!-- Toolbar: search + global enabled count -->
    <div class="rp-toolbar">
      <div class="rp-search">
        <i class="fa-solid fa-magnifying-glass rp-search__icon"></i>
        <input type="text" class="rp-search__input" v-model="search"
               :placeholder="$t('operations.search') + ' ' + $t('columns.permissions').toLowerCase() + '…'"/>
        <i v-if="search" class="fa-solid fa-xmark rp-search__clear" @click="search = ''"></i>
      </div>
      <div class="rp-total">
        <i class="fa-solid fa-shield-halved mr-50"></i>
        <span class="rp-total__num">{{ totalEnabled }}</span>
        <span class="rp-total__sep">/</span>
        <span>{{ totalCount }}</span>
      </div>
    </div>

    <div class="rp-groups">
      <div v-for="category in visibleCategories" :key="category" class="rp-group">
        <!-- Module header -->
        <div class="rp-group__header">
          <div class="rp-group__title">
            <span class="rp-group__icon"><i :class="iconForCategory(category)"></i></span>
            {{ allPermissionsByCategory[category].title }}
          </div>
          <div class="rp-group__meta">
            <span class="rp-group__count" :class="{ 'is-full': allEnabledInCategory(category) && enabledInCategory(category) > 0 }">
              {{ enabledInCategory(category) }} / {{ allPermissionsByCategory[category].permissions.length }}
            </span>
            <button type="button" class="rp-group__toggle"
                    :title="allEnabledInCategory(category) ? $t('operations.disableAll') : $t('operations.enableAll')"
                    @click="setAllInCategory(category)">
              <i class="fa-solid" :class="allEnabledInCategory(category) ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
            </button>
          </div>
        </div>

        <!-- Permission grid -->
        <div class="rp-group__grid">
          <div v-for="permission in filteredPermissions(category)" :key="permission.name"
               class="rp-item" :class="{ 'is-on': permissionSelection[permission.name] }"
               @click="toggle(permission.name)">
            <div class="rp-item__text">
              <div class="rp-item__desc">{{ permission.description || permission.name }}</div>
              <div class="rp-item__name">{{ permission.name }}</div>
            </div>
            <b-form-checkbox class="rp-item__switch" v-model="permissionSelection[permission.name]"
                             switch @change="updatePermissions" @click.native.stop/>
          </div>
        </div>
      </div>

      <div v-if="visibleCategories.length === 0" class="rp-empty">
        <i class="fa-solid fa-magnifying-glass"></i>
        <span>{{ $t('operations.noData') }}</span>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'PermissionsTable',
  props: ['value'],
  data: () => ({
    permissionSelection: {},
    search: '',
  }),
  watch: {
    value() {
      this.refresh()
    }
  },
  created() {
    this.refresh()
  },
  computed: {
    permissions: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    allPermissionsByCategory() {
      return this.$t('permissions')
    },
    permissionsList() {
      // Key off the OBJECT KEYS, never the `type` field — a locale file that
      // (wrongly) translates `type` would otherwise break every lookup and
      // blank the whole table in that language.
      const byCategory = this.allPermissionsByCategory
      return Object.keys(byCategory)
          .sort((k1, k2) => byCategory[k1].index - byCategory[k2].index)
    },
    allPermissions() {
      let permissions = {}
      this.permissionsList.forEach(category => {
        this.allPermissionsByCategory[category].permissions.forEach(permission => {
          permissions[permission.name] = permission
        })
      })
      return permissions
    },
    // Categories that still have at least one permission matching the search.
    visibleCategories() {
      return this.permissionsList.filter(cat => this.filteredPermissions(cat).length > 0)
    },
    totalCount() {
      return Object.keys(this.allPermissions).length
    },
    totalEnabled() {
      return Object.values(this.permissionSelection).filter(Boolean).length
    },
  },
  methods: {
    refresh() {
      let permissions = this.permissions || [];
      this.permissionSelection = Object.keys(this.allPermissions).map(key => ({ name: key, value: permissions.includes(key) }))
          .reduce((map, obj) => {
            map[obj.name] = obj.value
            return map
          }, {})

    },
    matchesSearch(permission) {
      if (!this.search) return true
      const q = this.search.toLowerCase()
      return (permission.name || '').toLowerCase().includes(q)
          || (permission.description || '').toLowerCase().includes(q)
    },
    filteredPermissions(category) {
      return this.allPermissionsByCategory[category].permissions.filter(p => this.matchesSearch(p))
    },
    enabledInCategory(category) {
      return this.allPermissionsByCategory[category].permissions.filter(p => this.permissionSelection[p.name]).length
    },
    allEnabledInCategory(category) {
      let perms = this.allPermissionsByCategory[category].permissions
      return perms.length > 0 && perms.every(p => this.permissionSelection[p.name])
    },
    toggle(name) {
      this.$set(this.permissionSelection, name, !this.permissionSelection[name])
      this.updatePermissions()
    },
    setAllInCategory(category) {
      let permissionsCategory = this.allPermissionsByCategory[category]
      let isAllTrue = permissionsCategory.permissions.every(permission => this.permissionSelection[permission.name])
      permissionsCategory.permissions.forEach(permission => {
        this.$set(this.permissionSelection, permission.name, !isAllTrue)
      })
      this.updatePermissions()
    },
    updatePermissions() {
      this.permissions = Object.keys(this.permissionSelection)
          .filter(key => this.permissionSelection[key])
    },
    iconForCategory(category) {
      const map = {
        CALENDAR: 'fa-solid fa-calendar-days',
        ORG: 'fa-solid fa-building',
        BU: 'fa-solid fa-sitemap',
        USER: 'fa-solid fa-user',
        USER_ROLES: 'fa-solid fa-user-shield',
        ROLES: 'fa-solid fa-user-shield',
        AFFILIATE: 'fa-solid fa-handshake',
        CLIENT: 'fa-solid fa-users',
        ACCOUNT: 'fa-solid fa-wallet',
        TRADE: 'fa-solid fa-chart-line',
        TRADING: 'fa-solid fa-chart-line',
        TRADING_SYSTEM: 'fa-solid fa-gears',
        MONETARY: 'fa-solid fa-coins',
        MARKETING: 'fa-solid fa-bullhorn',
        KYC: 'fa-solid fa-id-card',
        DOCUMENT: 'fa-solid fa-file-lines',
        SMTP: 'fa-solid fa-envelope',
        MAIL_TEMPLATE: 'fa-solid fa-envelope-open-text',
        CLIENT_STATUS: 'fa-solid fa-tags',
        WHITELIST: 'fa-solid fa-shield-halved',
        TICKET: 'fa-solid fa-ticket',
        MONITORING: 'fa-solid fa-desktop',
        EXPORT: 'fa-solid fa-file-export',
        TASK: 'fa-solid fa-list-check',
        HIERARCHY: 'fa-solid fa-sitemap',
        PSP: 'fa-solid fa-credit-card',
      }
      return map[category] || 'fa-solid fa-shield-halved'
    },
  }
}
</script>


<style scoped lang="scss">
.rp {
  --rp-accent: #7367f0;
  --rp-bg: #ffffff;
  --rp-bg-soft: #f8f8fc;
  --rp-border: #ebe9f1;
  --rp-text: #5e5873;
  --rp-text-muted: #b9b9c3;
}
.dark-layout .rp {
  --rp-bg: #283046;
  --rp-bg-soft: #212536;
  --rp-border: #3b4253;
  --rp-text: #d0d2d6;
  --rp-text-muted: #676d7d;
}

/* Toolbar */
.rp-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.rp-search {
  position: relative;
  flex: 1 1 260px;
  min-width: 200px;
  &__icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--rp-text-muted);
    font-size: 0.85rem;
  }
  &__input {
    width: 100%;
    height: 40px;
    padding: 0 34px 0 34px;
    border: 1px solid var(--rp-border);
    border-radius: 10px;
    background: var(--rp-bg);
    color: var(--rp-text);
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
    &:focus {
      border-color: var(--rp-accent);
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
    }
  }
  &__clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--rp-text-muted);
    cursor: pointer;
    &:hover { color: var(--rp-accent); }
  }
}
.rp-total {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 10px;
  background: rgba(115, 103, 240, 0.1);
  color: var(--rp-accent);
  font-weight: 700;
  font-size: 0.9rem;
  &__num { font-size: 1rem; }
  &__sep { margin: 0 0.3rem; opacity: 0.6; }
}

/* Module cards */
.rp-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(420px, 100%), 1fr));
  gap: 1rem;
}
.rp-group {
  border: 1px solid var(--rp-border);
  border-radius: 14px;
  background: var(--rp-bg);
  overflow: hidden;
  box-shadow: 0 4px 16px -12px rgba(20, 24, 50, 0.25);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.09), rgba(115, 103, 240, 0.02));
    border-bottom: 1px solid var(--rp-border);
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-weight: 700;
    font-size: 0.95rem;
    color: var(--rp-text);
  }
  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: rgba(115, 103, 240, 0.14);
    color: var(--rp-accent);
    font-size: 0.85rem;
  }
  &__meta {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }
  &__count {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--rp-text-muted);
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--rp-bg-soft);
    &.is-full { color: #28c76f; background: rgba(40, 199, 111, 0.12); }
  }
  &__toggle {
    border: none;
    background: transparent;
    color: var(--rp-accent);
    font-size: 1.35rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    transition: transform 0.15s ease;
    &:hover { transform: scale(1.08); }
    &:focus { outline: none; }
    .fa-toggle-off { color: var(--rp-text-muted); }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.35rem;
    padding: 0.6rem;
  }
}

/* Permission row */
.rp-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover { background: var(--rp-bg-soft); }
  &.is-on {
    background: rgba(115, 103, 240, 0.08);
    border-color: rgba(115, 103, 240, 0.25);
  }

  &__text { flex: 1; min-width: 0; }
  &__desc {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--rp-text);
    line-height: 1.25;
  }
  &__name {
    font-size: 0.68rem;
    font-family: 'SFMono-Regular', Consolas, monospace;
    color: var(--rp-text-muted);
    letter-spacing: 0.2px;
    margin-top: 1px;
  }
  &__switch {
    flex-shrink: 0;
    margin: 0;
    ::v-deep .custom-control-label { cursor: pointer; }
  }
}

.rp-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 3rem 1rem;
  color: var(--rp-text-muted);
  i { font-size: 1.6rem; display: block; margin-bottom: 0.6rem; opacity: 0.6; }
}
</style>
